import { useMemo } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

export interface DaysOfWeek {
  day: string;
  closed: boolean;
  openFrom: string;
  openTo: string;
}

export interface OpeningHours {
  businessHoursID: number;
  validFrom: string;
  validTo: string;
  weekStart: string;
  weekEnd: string;
  daysOfWeek: DaysOfWeek[];
}

export interface Outlet {
  businessID: number;
  name: string;
  faculty: string;
  department: string;
  email: string;
  telephone: string;
  photoURL: string;
  imageURL: string;
  websiteURL: string;
  latitude: number;
  longitude: number;
  description: string;
  shortDescription: string;
  isOpen: boolean;
  syscode: number;
  openingHours: OpeningHours;
  tags: string[];
}

export const useOutlets = () => {
  return useQuery<Outlet[]>(["GetOutlets"], async () => {
    const response = await fetch(
      "https://www.e-res.lancaster.ac.uk/DirectoryWebAPI/api/v2/Business/All/"
    );
    if (!response.ok) throw new Error("Failed fetching outlets.");
    return response.json();
  });
};

export const useOutletsSearch = (query: string) => {
  const outlets = useOutlets();

  const searchResults = useMemo(() => {
    return query.length > 0
      ? outlets.data?.filter(
          (outlet) =>
            outlet.name.toLowerCase().includes(query.toLowerCase()) ||
            outlet.tags.some((t) =>
              t.toLowerCase().includes(query.toLowerCase())
            )
        ) ?? []
      : [];
  }, [outlets, query]);

  return { ...outlets, data: searchResults } as UseQueryResult<Outlet[]>;
};

export const useOutlet = (businessID: string) => {
  const query = useOutlets();

  const outlet = useMemo(() => {
    if (query.data) {
      return query.data.find((o) => o.businessID === parseInt(businessID));
    }
  }, [query, businessID]);

  return { ...query, data: outlet };
};
